/**
 * Turn untrusted recipe-reader output into a draft the form can show.
 *
 * Runs on the server AND again in the client. Nothing here trusts its input:
 * an AI will occasionally invent a plausible id ("beef-minced"), and an id
 * that is not in the catalogue throws in the engine the first time the list
 * is built. Every id is checked; anything unproven becomes an extra or an
 * empty amount the person has to fill in. Nothing is saved from here — the
 * form is always the last step.
 */
import { UNITS_FOR, emptyDraft, type DraftLine, type EntryUnit, type RecipeDraft } from './recipeDraft.js';
import type { Ingredient } from './types.js';

export type ImportUnit = EntryUnit | 'other';
export const IMPORT_UNITS: readonly ImportUnit[] = ['g', 'kg', 'ml', 'l', 'tsp', 'tbsp', 'each', 'other'];

export interface ImportedLine {
  text: string;
  name: string;
  amount: number | null;
  unit: ImportUnit;
  unitLabel: string;
  ingredientId: string | null;
}

export interface ImportedRecipe {
  name: string;
  serves: number | null;
  lines: ImportedLine[];
  method: string[];
}

const MAX_LINES = 60;
const MAX_STEPS = 30;

const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
const amountOf = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null;
const record = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};

export function validateImport(
  raw: unknown,
  ingredients: ReadonlyMap<string, Ingredient>,
  resolveName: (name: string) => Ingredient | null,
): { draft: RecipeDraft; warnings: string[] } {
  const draft = emptyDraft();
  const warnings: string[] = [];
  if (!raw || typeof raw !== 'object') {
    return { draft, warnings: ["Couldn't read a recipe from that."] };
  }
  const input = record(raw);

  draft.name = str(input['name']);
  if (draft.name === '') warnings.push("Couldn't find the recipe's name. Add one.");

  const serves = input['serves'];
  if (typeof serves === 'number' && Number.isInteger(serves) && serves >= 1 && serves <= 20) {
    draft.serves = serves;
  } else {
    warnings.push("Couldn't tell how many it serves, so it's set to 4. Check it.");
  }

  draft.method = (Array.isArray(input['method']) ? input['method'] : [])
    .map(str).filter((s) => s.length > 0).slice(0, MAX_STEPS);

  const lines = (Array.isArray(input['lines']) ? input['lines'] : []).slice(0, MAX_LINES);
  for (const rawLine of lines) {
    const l = record(rawLine);
    const name = str(l['name']);
    const text = str(l['text']) || name;
    const amount = amountOf(l['amount']);
    const unitRaw = str(l['unit']) as ImportUnit;
    const unit: ImportUnit = IMPORT_UNITS.includes(unitRaw) ? unitRaw : 'other';
    const id = str(l['ingredientId']);

    const ingredient = (id ? ingredients.get(id) : undefined) ?? (name ? resolveName(name) : null);
    if (!ingredient) {
      const label = name || text;
      if (label === '') continue;
      const unitLabel = unit === 'other' ? str(l['unitLabel']) : unit;
      draft.lines.push({ kind: 'extra', label, amount, unitLabel, aisle: 'ambient' } satisfies DraftLine);
      warnings.push(`"${label}" isn't in the ingredient list, so it's added as an extra item.`);
      continue;
    }

    const allowed = UNITS_FOR[ingredient.baseUnit];
    const fits = unit !== 'other' && allowed.includes(unit);
    draft.lines.push({
      kind: 'ingredient',
      ingredientId: ingredient.id,
      amount: fits ? amount : null,
      unit: fits ? (unit as EntryUnit) : (allowed[0] ?? 'g'),
      prepNote: null,
      optional: false,
    } satisfies DraftLine);
    if (!fits && amount !== null) warnings.push(`Check the amount for ${ingredient.name} ("${text}").`);
  }

  return { draft, warnings };
}
